/**
 * Login page (TASK-632: renamed from DemoLoginPage — this is THE general login,
 * served at /login with /demo-login kept as a route alias one release).
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import KielisLogo from '../../assets/KielisLogo-no-bg.png';
import { useLogin } from '../hooks/useLogin';
import { useAccount } from '../contexts/AccountContext';
import LanguageSelector from '../components/LanguageSelector';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(['auth', 'common']);
  const { isAuthenticated } = useAccount();
  const redirectTo = (location.state as any)?.from || '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const { login, isLoading } = useLogin();

  // Redirect to dashboard when authenticated (TASK-168)
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please enter both email and password');
      return;
    }

    try {
      const result = await login(email, password);

      if (result.status !== 'ok') {
        // TASK-631: tenant requires 2FA — continue on the challenge page.
        navigate('/login/2fa', {
          state: {
            challengeToken: result.challengeToken,
            status: result.status,
            email,
            from: redirectTo,
          },
        });
        return;
      }

      toast.success('Welcome to Kielis');
      // Navigation handled by useEffect when isAuthenticated becomes true
    } catch (error: any) {
      console.error('Login failed:', error);
      const errorMessage =
        error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-kielis-glacier to-slate-100 flex items-center justify-center p-4">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>

      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <img
            src={KielisLogo}
            alt="Kielis"
            className="h-16 md:h-20 mx-auto mb-6"
          />
          <h1 className="text-2xl md:text-3xl font-bold text-kielis-navy mb-2">
            {t('auth:login.title')}
          </h1>
          <p className="text-sm md:text-base text-slate-600">
            {t('auth:login.subtitle')}
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl shadow-lg p-8 border border-slate-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                {t('auth:login.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg
                           focus:outline-none focus:ring-2 focus:ring-kielis-navy focus:border-transparent
                           transition-all disabled:bg-slate-100 disabled:cursor-not-allowed"
                placeholder="your@email.com"
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                {t('auth:login.password')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg
                             focus:outline-none focus:ring-2 focus:ring-kielis-navy focus:border-transparent
                             transition-all disabled:bg-slate-100 disabled:cursor-not-allowed"
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700
                             disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {/* TASK-629: public forgot-password entry */}
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-kielis-navy hover:underline"
                  disabled={isLoading}
                >
                  Forgot password?
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-kielis-navy text-white py-3 rounded-lg font-semibold
                         hover:bg-opacity-90 disabled:bg-slate-300 disabled:cursor-not-allowed
                         transition-all duration-200 shadow-sm hover:shadow-md"
            >
              {isLoading ? t('common:labels.loading') : t('auth:login.submit')}
            </button>
          </form>

        </div>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-6">
          {t('navigation:branding.subtitle')}
        </p>
      </div>
    </div>
  );
}
